import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {Button, ButtonGroup, VStack} from '@chakra-ui/react'
import {RETURN_REASONS} from '@cypro/engine/database/schema'
import {ReturnToPharmacyEventBody} from '@cypro/engine/mutators'
import {BasicDataResources, PatientTherapySequence} from '@cypro/engine/schema'
import {dialog, DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {revalidateLogic} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

function onClose() {
  dialog.close('return-to-pharmacy-dialog', true)
}

export function ReturnToPharmacyDialog({sequence}: {sequence: PatientTherapySequence}) {
  const {t} = useTranslate('common')
  const {sendEvent} = useTherapySequenceActions(sequence)

  const {items: stockLocations} = useGetBasicDataItems({resource: BasicDataResources.stockLocations})
  const stockLocationItems = mapBasicDataItemsToSelectItems(stockLocations, {includeCode: false})

  const form = useAppForm({
    defaultValues: {
      reason: RETURN_REASONS[0],
      stockLocationId: '',
      note: '',
    } as ReturnToPharmacyEventBody,
    validators: {
      onDynamic: ReturnToPharmacyEventBody,
    },
    validationLogic: revalidateLogic(),
    async onSubmit() {
      return await createReturn()
    },
  })

  async function createReturn() {
    const values = form.state.values

    await sendEvent({
      id: sequence.id,
      event: {
        type: 'RETURN_TO_PHARMACY',
        body: {
          reason: values.reason,
          stockLocationId: values.stockLocationId,
          note: values.note?.trim() || undefined,
        },
      },
    })
    onClose()
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('return_to_pharmacy')}</DialogHeader>

        <DialogBody minHeight="none">
          <VStack gap="5" width="100%" alignItems="flex-start">
            <form.AppField name="reason" mode="value">
              {field => (
                <field.RadioCard
                  label={t('reason')}
                  items={RETURN_REASONS.map(r => ({label: t(`return_reason.${r}`), value: r}))}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="stockLocationId" mode="value">
              {field => (
                <field.Select
                  label={t('stock_location')}
                  placeholder={t('select_location')}
                  items={stockLocationItems}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="note" mode="value">
              {field => <field.Textarea placeholder={t('add_note_or_observation')} rows={3} label={t('notes')} />}
            </form.AppField>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <form.SubmitButton colorPalette="cyan">{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
